import React, { useState } from 'react';
import './LayoutMockups.css';

// Sample content for different page types
const sampleContent = {
  blog: {
    type: 'blog',
    title: "Understanding Movement Patterns in Parkinson's Disease",
    subtitle: "How wearable technology is revolutionizing early detection and monitoring",
    author: {
      name: "Dr. Sarah Chen",
      credentials: "PhD, MSc",
      role: "Principal Researcher",
      institution: "Imperial College London",
      avatar: null,
      verified: true
    },
    publishedAt: "January 15, 2026",
    readTime: "8 min read",
    category: "Research Insights",
    tags: ["Parkinson's", "Wearables", "Gait Analysis", "Early Detection"],
    heroImage: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1200",
    peerReviewed: true,
    reviewers: 3,
    endorsements: 47,
    content: `
      <p class="lead">Recent advances in wearable sensor technology have opened new possibilities for understanding and monitoring movement disorders.</p>
      <h2>The Challenge of Early Detection</h2>
      <p>Parkinson's disease affects over 10 million people worldwide, yet diagnosis often comes years after the onset of subtle motor symptoms.</p>
      <blockquote>"We're seeing movement patterns that would be impossible to detect in a 15-minute clinic visit."</blockquote>
      <h2>Continuous Monitoring: A New Paradigm</h2>
      <p>Wearable devices can capture thousands of data points per day:</p>
      <ul>
        <li>Gait variability and stride patterns</li>
        <li>Tremor frequency and amplitude</li>
        <li>Postural stability during daily activities</li>
      </ul>
    `,
    references: [
      { id: 1, text: "Smith et al. (2025). Wearable sensors in movement disorders. Nature Digital Medicine." },
      { id: 2, text: "Johnson & Williams (2024). Gait analysis algorithms. IEEE J-BHI." }
    ]
  },
  bio: {
    type: 'bio',
    title: "Dr. James Mitchell",
    subtitle: "Specialist Physiotherapist & Movement Health Expert",
    author: {
      name: "Dr. James Mitchell",
      credentials: "DPT, MSc, MCSP",
      role: "Lead Physiotherapist",
      institution: "London Movement Clinic",
      avatar: null,
      verified: true
    },
    publishedAt: "Member since 2023",
    readTime: "15 years experience",
    category: "Physiotherapist",
    tags: ["Sports Rehab", "Neurological", "Elderly Care", "Post-Surgery"],
    heroImage: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200",
    peerReviewed: false,
    reviewers: 0,
    endorsements: 124,
    content: `
      <p class="lead">Dedicated to helping patients regain mobility and improve quality of life through evidence-based physiotherapy.</p>
      <h2>About Me</h2>
      <p>With over 15 years of experience in clinical and sports physiotherapy, I specialise in movement disorders, post-operative rehabilitation, and chronic pain management.</p>
      <h2>Qualifications</h2>
      <ul>
        <li>Doctor of Physical Therapy - King's College London</li>
        <li>MSc Sports Medicine - UCL</li>
        <li>Certified Manual Therapist</li>
      </ul>
      <h2>Areas of Expertise</h2>
      <p>My practice focuses on holistic, patient-centred care combining traditional techniques with modern technology.</p>
    `,
    references: []
  },
  corporate: {
    type: 'corporate',
    title: "PhysioActivity for Enterprise",
    subtitle: "Transform your workplace wellness with data-driven movement health programmes",
    author: {
      name: "PhysioActivity",
      credentials: "",
      role: "Corporate Solutions",
      institution: "PhysioActivity Ltd",
      avatar: null,
      verified: true
    },
    publishedAt: "Updated February 2026",
    readTime: "3 min read",
    category: "Corporate Wellness",
    tags: ["Workplace Health", "Employee Wellness", "ROI", "Productivity"],
    heroImage: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200",
    peerReviewed: false,
    reviewers: 0,
    endorsements: 89,
    content: `
      <p class="lead">Reduce absenteeism by 40% and boost employee satisfaction with our comprehensive workplace movement health programme.</p>
      <h2>Why Movement Health Matters</h2>
      <p>Musculoskeletal disorders are the leading cause of workplace absence. Our programmes address this through preventive care and early intervention.</p>
      <h2>Our Enterprise Solutions</h2>
      <ul>
        <li>On-site physiotherapy assessments</li>
        <li>Digital health monitoring via wearables</li>
        <li>Customised exercise programmes</li>
        <li>Management dashboards and reporting</li>
      </ul>
      <blockquote>"PhysioActivity reduced our MSK-related absences by 45% in the first year." - HR Director, Fortune 500 Company</blockquote>
    `,
    references: []
  },
  research: {
    type: 'research',
    title: "SteadyStride Clinical Trial",
    subtitle: "A randomized controlled trial evaluating wearable-guided gait training for fall prevention in older adults",
    author: {
      name: "Prof. Elizabeth Warren",
      credentials: "MD, PhD, FRCP",
      role: "Principal Investigator",
      institution: "University of Southampton",
      avatar: null,
      verified: true
    },
    publishedAt: "Trial Start: March 2026",
    readTime: "12 month duration",
    category: "Clinical Trial",
    tags: ["Falls Prevention", "Older Adults", "Gait Training", "RCT"],
    heroImage: "https://images.unsplash.com/photo-1576671081837-49000212a370?w=1200",
    peerReviewed: true,
    reviewers: 5,
    endorsements: 34,
    content: `
      <p class="lead">This study evaluates whether continuous gait monitoring combined with personalised feedback can reduce fall risk in adults aged 65+.</p>
      <h2>Study Objectives</h2>
      <p>Primary outcome: Reduction in fall incidence over 12 months. Secondary outcomes include gait parameters, quality of life, and healthcare utilisation.</p>
      <h2>Eligibility Criteria</h2>
      <ul>
        <li>Adults aged 65 years or older</li>
        <li>History of at least one fall in the past 12 months</li>
        <li>Ability to walk independently (with or without aid)</li>
        <li>Smartphone ownership</li>
      </ul>
      <h2>Study Design</h2>
      <p>Participants will be randomized 1:1 to receive either the SteadyStride intervention or usual care.</p>
    `,
    references: [
      { id: 1, text: "WHO Global Report on Falls Prevention in Older Age (2024)" },
      { id: 2, text: "NICE Guidelines: Falls Assessment and Prevention (2023)" }
    ]
  }
};

// Layout Components
const LinkedInLayout = ({ content }) => (
  <div className="layout-linkedin">
    <div className="linkedin-cover">
      <img src={content.heroImage} alt="" />
    </div>
    <div className="linkedin-header-card">
      <div className="linkedin-avatar">
        <div className="linkedin-avatar-placeholder">
          {content.author.name.split(' ').map(n => n[0]).join('')}
        </div>
        {content.author.verified && <span className="linkedin-verified">✓</span>}
      </div>
      <div className="linkedin-meta">
        <h1>{content.title}</h1>
        <p className="linkedin-author-line">
          {content.author.name}{content.author.credentials && `, ${content.author.credentials}`} • {content.author.role}
        </p>
        <p className="linkedin-institution">{content.author.institution}</p>
        <div className="linkedin-badges">
          {content.peerReviewed && (
            <span className="badge badge-verified">✓ Peer Reviewed</span>
          )}
          <span className="badge badge-category">{content.category}</span>
          <span className="badge badge-time">{content.readTime}</span>
        </div>
      </div>
      <div className="linkedin-stats">
        <div className="stat">
          <span className="stat-value">{content.endorsements}</span>
          <span className="stat-label">Endorsements</span>
        </div>
        {content.reviewers > 0 && (
          <div className="stat">
            <span className="stat-value">{content.reviewers}</span>
            <span className="stat-label">Reviewers</span>
          </div>
        )}
      </div>
    </div>
    <div className="linkedin-section">
      <h2 className="linkedin-section-title">About</h2>
      <div className="linkedin-content" dangerouslySetInnerHTML={{ __html: content.content }} />
    </div>
    <div className="linkedin-section">
      <h2 className="linkedin-section-title">Topics</h2>
      <div className="linkedin-tags">
        {content.tags.map(tag => (
          <span key={tag} className="linkedin-tag">{tag}</span>
        ))}
      </div>
    </div>
  </div>
);

const MagazineLayout = ({ content }) => (
  <div className="layout-magazine">
    <div className="magazine-hero">
      <img src={content.heroImage} alt="" />
      <div className="magazine-hero-overlay">
        <div className="magazine-hero-content">
          <span className="magazine-category">{content.category}</span>
          <h1>{content.title}</h1>
          <p className="magazine-subtitle">{content.subtitle}</p>
          <div className="magazine-byline">
            <div className="magazine-author-avatar">
              {content.author.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div className="magazine-author-info">
              <span className="magazine-author-name">{content.author.name}</span>
              <span className="magazine-meta">{content.publishedAt} · {content.readTime}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <article className="magazine-article">
      {content.peerReviewed && (
        <div className="magazine-peer-badge">
          <strong>✓ Peer Reviewed Article</strong>
          <span>Reviewed by {content.reviewers} CMPX community experts</span>
        </div>
      )}
      <div className="magazine-content" dangerouslySetInnerHTML={{ __html: content.content }} />
      <div className="magazine-tags">
        {content.tags.map(tag => (
          <span key={tag} className="magazine-tag">#{tag}</span>
        ))}
      </div>
    </article>
    <div className="magazine-author-card">
      <div className="magazine-author-avatar-lg">
        {content.author.name.split(' ').map(n => n[0]).join('')}
      </div>
      <div>
        <h3>Written by {content.author.name}</h3>
        <p>{content.author.role} at {content.author.institution}</p>
        <button className="magazine-follow-btn">Follow</button>
      </div>
    </div>
  </div>
);

const MediumLayout = ({ content }) => (
  <div className="layout-medium">
    <header className="medium-header">
      <span className="medium-category">{content.category}</span>
      <h1>{content.title}</h1>
      <p className="medium-subtitle">{content.subtitle}</p>
      <div className="medium-author-row">
        <div className="medium-author-avatar">
          {content.author.name.split(' ').map(n => n[0]).join('')}
        </div>
        <div className="medium-author-info">
          <div className="medium-author-name">
            {content.author.name}
            {content.author.verified && <span className="medium-verified">✓</span>}
          </div>
          <div className="medium-meta">
            {content.publishedAt} · {content.readTime}
            {content.peerReviewed && <span className="medium-peer-dot"> · Peer Reviewed ✓</span>}
          </div>
        </div>
      </div>
    </header>
    <figure className="medium-hero">
      <img src={content.heroImage} alt="" />
    </figure>
    <article className="medium-content" dangerouslySetInnerHTML={{ __html: content.content }} />
    <div className="medium-tags">
      {content.tags.map(tag => (
        <span key={tag} className="medium-tag">{tag}</span>
      ))}
    </div>
    <div className="medium-engagement">
      <button className="medium-clap">👏 {content.endorsements}</button>
      <button className="medium-share">Share</button>
      <button className="medium-bookmark">Bookmark</button>
    </div>
    <div className="medium-author-bio">
      <div className="medium-author-avatar-lg">
        {content.author.name.split(' ').map(n => n[0]).join('')}
      </div>
      <div>
        <p className="medium-written-by">Written by</p>
        <h3>{content.author.name}{content.author.credentials && `, ${content.author.credentials}`}</h3>
        <p>{content.author.role} · {content.author.institution}</p>
      </div>
    </div>
  </div>
);

const AcademicLayout = ({ content }) => (
  <div className="layout-academic">
    <header className="academic-header">
      <div className="academic-journal">CMPX {content.category}</div>
      <h1>{content.title}</h1>
      <div className="academic-authors">
        <span className="academic-author">{content.author.name}<sup>1</sup></span>
      </div>
      <div className="academic-affiliations">
        <span><sup>1</sup> {content.author.institution}</span>
      </div>
      <div className="academic-meta-grid">
        <div className="academic-meta-item">
          <span className="academic-meta-label">Published</span>
          <span>{content.publishedAt}</span>
        </div>
        <div className="academic-meta-item">
          <span className="academic-meta-label">Category</span>
          <span>{content.category}</span>
        </div>
        <div className="academic-meta-item">
          <span className="academic-meta-label">Duration</span>
          <span>{content.readTime}</span>
        </div>
        <div className="academic-meta-item">
          <span className="academic-meta-label">Peer Review</span>
          <span className="academic-status">{content.peerReviewed ? '✓ Verified' : 'N/A'}</span>
        </div>
      </div>
    </header>
    <section className="academic-abstract">
      <h2>Abstract</h2>
      <p>{content.subtitle}</p>
      <div className="academic-keywords">
        <strong>Keywords:</strong> {content.tags.join(', ')}
      </div>
    </section>
    <article className="academic-content">
      <div className="academic-body" dangerouslySetInnerHTML={{ __html: content.content }} />
    </article>
    {content.references.length > 0 && (
      <section className="academic-references">
        <h2>References</h2>
        <ol>
          {content.references.map(ref => (
            <li key={ref.id}>{ref.text}</li>
          ))}
        </ol>
      </section>
    )}
    {content.peerReviewed && (
      <aside className="academic-peer-review">
        <h3>Peer Review Statement</h3>
        <p>This article has been reviewed by {content.reviewers} independent experts in the CMPX community.</p>
      </aside>
    )}
  </div>
);

const CorporateLayout = ({ content }) => (
  <div className="layout-corporate">
    <header className="corporate-header">
      <div className="corporate-header-content">
        <span className="corporate-eyebrow">{content.category}</span>
        <h1>{content.title}</h1>
        <p className="corporate-subtitle">{content.subtitle}</p>
        <div className="corporate-cta-row">
          <button className="corporate-btn-primary">Get Started</button>
          <button className="corporate-btn-secondary">Learn More</button>
        </div>
      </div>
      <div className="corporate-header-image">
        <img src={content.heroImage} alt="" />
      </div>
    </header>
    <div className="corporate-stats-bar">
      <div className="corporate-stat">
        <span className="corporate-stat-value">{content.endorsements}</span>
        <span className="corporate-stat-label">Endorsements</span>
      </div>
      <div className="corporate-stat">
        <span className="corporate-stat-value">{content.reviewers || 'n/a'}</span>
        <span className="corporate-stat-label">Expert Reviewers</span>
      </div>
      <div className="corporate-stat">
        <span className="corporate-stat-value">{content.readTime}</span>
        <span className="corporate-stat-label">Read Time</span>
      </div>
      <div className="corporate-stat">
        <span className="corporate-stat-value">{content.peerReviewed ? '✓' : 'n/a'}</span>
        <span className="corporate-stat-label">Peer Reviewed</span>
      </div>
    </div>
    <section className="corporate-section">
      <div className="corporate-section-grid">
        <div className="corporate-main-content" dangerouslySetInnerHTML={{ __html: content.content }} />
        <aside className="corporate-sidebar">
          <div className="corporate-author-card">
            <h3>About</h3>
            <div className="corporate-author-avatar">
              {content.author.name.split(' ').map(n => n[0]).join('')}
            </div>
            <h4>{content.author.name}</h4>
            <p className="corporate-author-role">{content.author.role}</p>
            <p className="corporate-author-inst">{content.author.institution}</p>
          </div>
          <div className="corporate-tags-card">
            <h3>Topics</h3>
            <div className="corporate-tags">
              {content.tags.map(tag => (
                <span key={tag} className="corporate-tag">{tag}</span>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </section>
  </div>
);

// Current Site Layout - shows iframe of actual pages
const CurrentSiteLayout = ({ contentType }) => {
  const pageUrls = {
    bio: '/profile',
    corporate: '/solutions',
    research: '/research',
    blog: '/profile' // No blog page yet, show profile as placeholder
  };

  const pageLabels = {
    bio: 'ProfilePage - Current Bio/Physiotherapist Layout',
    corporate: 'SolutionsPage - Current Corporate Layout',
    research: 'StudyPage - Current Research Layout',
    blog: 'No blog page exists yet (showing ProfilePage)'
  };

  return (
    <div className="layout-current">
      <div className="current-header">
        <div className="current-badge">📍 CURRENT LIVE LAYOUT</div>
        <h2>{pageLabels[contentType]}</h2>
        <p>This is how the page currently looks on the website</p>
        <a
          href={pageUrls[contentType]}
          target="_blank"
          rel="noopener noreferrer"
          className="current-open-btn"
        >
          Open in New Tab ↗
        </a>
      </div>
      <div className="current-iframe-container">
        <iframe
          src={pageUrls[contentType]}
          title={`Current ${contentType} page`}
          className="current-iframe"
        />
      </div>
      <div className="current-features">
        <h3>Current Features on this Page:</h3>
        {contentType === 'bio' && (
          <ul>
            <li>✓ Cover photo & avatar</li>
            <li>✓ Credentials & badges (HCPC, CSP)</li>
            <li>✓ About section with bio</li>
            <li>✓ <strong>Payment plans module</strong> (Bronze/Silver/Gold)</li>
            <li>✓ Reviews section</li>
            <li>✓ FAQs section</li>
            <li>✓ App download section</li>
          </ul>
        )}
        {contentType === 'corporate' && (
          <ul>
            <li>✓ Hero section with CTA</li>
            <li>✓ Benefits grid</li>
            <li>✓ How it works steps</li>
            <li>✓ Contact form</li>
          </ul>
        )}
        {contentType === 'research' && (
          <ul>
            <li>✓ Study header with status badge</li>
            <li>✓ Study overview grid</li>
            <li>✓ Eligibility criteria</li>
            <li>✓ Research team section</li>
            <li>✓ <strong>Enrollment modal</strong></li>
            <li>✓ Ethics & governance</li>
          </ul>
        )}
        {contentType === 'blog' && (
          <p className="current-note">⚠️ Blog pages don't exist yet - this is what we're designing!</p>
        )}
      </div>
    </div>
  );
};

const CardGridLayout = ({ content }) => {
  const items = [
    { ...content, id: 1 },
    { ...sampleContent.bio, id: 2 },
    { ...sampleContent.corporate, id: 3 },
    { ...sampleContent.research, id: 4 },
    { ...sampleContent.blog, id: 5, title: "Exercise Interventions for Neurological Conditions" },
    { ...sampleContent.bio, id: 6, title: "Dr. Emma Roberts - Sports Medicine" }
  ];

  return (
    <div className="layout-cardgrid">
      <header className="cardgrid-header">
        <h1>CMPX Content Hub</h1>
        <p>Insights from our community of movement health experts</p>
        <div className="cardgrid-filters">
          <button className="cardgrid-filter active">All</button>
          <button className="cardgrid-filter">Blog</button>
          <button className="cardgrid-filter">Research</button>
          <button className="cardgrid-filter">Bios</button>
          <button className="cardgrid-filter">Corporate</button>
        </div>
      </header>
      <div className="cardgrid-grid">
        {items.map((item, i) => (
          <article key={item.id} className={`cardgrid-card ${i === 0 ? 'cardgrid-featured' : ''}`}>
            <div className="cardgrid-card-image">
              <img src={item.heroImage} alt="" />
              <span className="cardgrid-category">{item.category}</span>
              {item.peerReviewed && <span className="cardgrid-verified">✓ Verified</span>}
            </div>
            <div className="cardgrid-card-content">
              <h2>{item.title}</h2>
              {i === 0 && <p>{item.subtitle}</p>}
              <div className="cardgrid-card-meta">
                <div className="cardgrid-author">
                  <span className="cardgrid-author-avatar">
                    {item.author.name.split(' ').map(n => n[0]).join('')}
                  </span>
                  <span>{item.author.name}</span>
                </div>
                <span className="cardgrid-time">{item.readTime}</span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

// Main Component
const LayoutMockups = () => {
  const [activeLayout, setActiveLayout] = useState('current');
  const [activeContent, setActiveContent] = useState('bio');
  const [controlsVisible, setControlsVisible] = useState(true);

  const layouts = [
    { id: 'current', name: 'Current', desc: 'Live site layout', icon: '📍', bestFor: 'Compare with new', highlight: true },
    { id: 'linkedin', name: 'LinkedIn', desc: 'Professional card-based', icon: '💼', bestFor: 'Profiles, Bios' },
    { id: 'magazine', name: 'Magazine', desc: 'Bold editorial hero', icon: '📰', bestFor: 'Featured articles' },
    { id: 'medium', name: 'Medium', desc: 'Clean, reading-focused', icon: '📝', bestFor: 'Long-form blogs' },
    { id: 'academic', name: 'Academic', desc: 'Journal-style format', icon: '🎓', bestFor: 'Research, Studies' },
    { id: 'corporate', name: 'Corporate', desc: 'Stats and CTAs', icon: '🏢', bestFor: 'Company pages' },
    { id: 'cardgrid', name: 'Card Grid', desc: 'Pinterest-style listing', icon: '🗂️', bestFor: 'Index pages' }
  ];

  const contentTypes = [
    { id: 'blog', name: 'Blog Post', icon: '📝' },
    { id: 'bio', name: 'Professional Bio', icon: '👤' },
    { id: 'corporate', name: 'Corporate Page', icon: '🏢' },
    { id: 'research', name: 'Research Study', icon: '🔬' }
  ];

  const renderLayout = (layoutId, content, contentType) => {
    const props = { content };
    switch (layoutId) {
      case 'current': return <CurrentSiteLayout contentType={contentType} />;
      case 'linkedin': return <LinkedInLayout {...props} />;
      case 'magazine': return <MagazineLayout {...props} />;
      case 'medium': return <MediumLayout {...props} />;
      case 'academic': return <AcademicLayout {...props} />;
      case 'corporate': return <CorporateLayout {...props} />;
      case 'cardgrid': return <CardGridLayout {...props} />;
      default: return <LinkedInLayout {...props} />;
    }
  };

  const currentContent = sampleContent[activeContent];
  const currentLayout = layouts.find(l => l.id === activeLayout);

  return (
    <div className="mockup-viewer">
      {/* Collapsible Control Panel */}
      <div className={`mockup-controls ${!controlsVisible ? 'collapsed' : ''}`}>
        <div className="mockup-header-row">
          <h1>📐 Layout Mockups</h1>
          <button
            className="mockup-toggle-btn"
            onClick={() => setControlsVisible(!controlsVisible)}
          >
            {controlsVisible ? '▲ Hide' : '▼ Show Controls'}
          </button>
        </div>

        {controlsVisible && (
          <div className="mockup-controls-body">
            {/* Content Type Selector */}
            <div className="mockup-section">
              <h2>Content Type</h2>
              <div className="mockup-content-btns">
                {contentTypes.map(ct => (
                  <button
                    key={ct.id}
                    className={`mockup-content-btn ${activeContent === ct.id ? 'active' : ''}`}
                    onClick={() => setActiveContent(ct.id)}
                  >
                    <span>{ct.icon}</span>
                    <span>{ct.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Layout Selector */}
            <div className="mockup-section">
              <h2>Layout Style</h2>
              <div className="mockup-layout-btns">
                {layouts.map(layout => (
                  <button
                    key={layout.id}
                    className={`mockup-layout-btn ${activeLayout === layout.id ? 'active' : ''} ${layout.highlight ? 'highlight' : ''}`}
                    onClick={() => setActiveLayout(layout.id)}
                  >
                    <span className="mockup-layout-icon">{layout.icon}</span>
                    <span className="mockup-layout-name">{layout.name}</span>
                    <span className="mockup-layout-desc">{layout.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Current Selection Info */}
            <div className="mockup-current-info">
              <span className="mockup-info-label">Viewing:</span>
              <span className="mockup-info-content">{contentTypes.find(c => c.id === activeContent)?.icon} {contentTypes.find(c => c.id === activeContent)?.name}</span>
              <span className="mockup-info-label">in</span>
              <span className="mockup-info-layout">{currentLayout?.icon} {currentLayout?.name} Layout</span>
            </div>
          </div>
        )}
      </div>

      {/* Floating Quick Switcher */}
      {!controlsVisible && (
        <div className="mockup-floating">
          <div className="mockup-floating-content">
            {contentTypes.map(ct => (
              <button
                key={ct.id}
                className={`mockup-float-btn ${activeContent === ct.id ? 'active' : ''}`}
                onClick={() => setActiveContent(ct.id)}
                title={ct.name}
              >
                {ct.icon}
              </button>
            ))}
          </div>
          <div className="mockup-floating-divider"></div>
          <div className="mockup-floating-layouts">
            {layouts.map(layout => (
              <button
                key={layout.id}
                className={`mockup-float-btn ${activeLayout === layout.id ? 'active' : ''}`}
                onClick={() => setActiveLayout(layout.id)}
                title={layout.name}
              >
                {layout.icon}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="mockup-preview">
        <div className="mockup-preview-label">
          {currentLayout?.icon} {currentLayout?.name}: {contentTypes.find(c => c.id === activeContent)?.name}
        </div>
        <div className="mockup-preview-content">
          {renderLayout(activeLayout, currentContent, activeContent)}
        </div>
      </div>

      {/* Recommendations */}
      <div className="mockup-recommendations">
        <h2>Recommended Layout by Content Type</h2>
        <table className="mockup-rec-table">
          <thead>
            <tr>
              <th>Content Type</th>
              <th>Recommended</th>
              <th>Also Works</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Blog Post</strong></td>
              <td>📰 Magazine / 📝 Medium</td>
              <td>💼 LinkedIn</td>
            </tr>
            <tr>
              <td><strong>Professional Bio</strong></td>
              <td>💼 LinkedIn</td>
              <td>📝 Medium</td>
            </tr>
            <tr>
              <td><strong>Corporate Page</strong></td>
              <td>🏢 Corporate</td>
              <td>📰 Magazine</td>
            </tr>
            <tr>
              <td><strong>Research Study</strong></td>
              <td>🎓 Academic</td>
              <td>💼 LinkedIn</td>
            </tr>
            <tr>
              <td><strong>Content Index</strong></td>
              <td>🗂️ Card Grid</td>
              <td>n/a</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LayoutMockups;
